/**
 * Upsell Popup — shows a product recommendation after add-to-cart.
 *
 * Two entry points, one shared popup UI:
 *   1. PDP:        window._onCartAdd(item)   — registered only when the viewed
 *                  product has a server-rendered #upsell-data block. Called by
 *                  cart-drawer.js after a successful add-to-cart.
 *   2. Collection: window.showUpsellPopup(addedItem, upsellData) — called by
 *                  assets/quick-add.js with the added line item + the card's
 *                  per-product upsell data (.qa-upsell in product-card-ab).
 *
 * In both cases: if the upsell product is not already in cart, the popup shows;
 * otherwise it falls through to openCart().
 *
 * Depends on:
 *   - #upsell-popup  container element (global, from snippets/upsell-popup.liquid)
 *   - #upsell-i18n   JSON block with translations (global)
 *   - #upsell-data   JSON block (PDP only — the viewed product's upsell)
 *   - window.openCart(), window.refreshCartDrawer() from cart-drawer.js
 */
(function() {
  'use strict';

  /* ── DOM references — the popup shell is global; bail only if it's missing ── */
  var popup = document.getElementById('upsell-popup');
  if (!popup) return;
  var panel = popup.querySelector('.upsell-popup__panel');
  if (!panel) return;

  /* ── PDP server-rendered upsell (absent on collection/other pages) ── */
  var pdpUpsellData = null;
  var upsellDataEl = document.getElementById('upsell-data');
  if (upsellDataEl) {
    try { pdpUpsellData = JSON.parse(upsellDataEl.textContent); }
    catch (e) { console.error('[upsell-popup] Failed to parse upsell data:', e); }
  }

  /* ── i18n strings — always-present #upsell-i18n block, German fallbacks ── */
  var i18nFromBlock = {};
  var i18nEl = document.getElementById('upsell-i18n');
  if (i18nEl) {
    try { i18nFromBlock = JSON.parse(i18nEl.textContent); }
    catch (e) { console.error('[upsell-popup] Failed to parse i18n:', e); }
  }
  var T = Object.assign({
    added: 'Hinzugefügt',
    recommended: 'Dazu empfohlen',
    add: 'Hinzufügen',
    to_cart: 'Zum Warenkorb',
    continue_shopping: 'Weiter shoppen',
    close: 'Schließen',
    try_again: 'Erneut versuchen',
    shipping_reached: 'Kostenloser Versand!',
    shipping_remaining_html: 'Noch <strong>%AMOUNT%</strong> bis zum kostenlosen Versand'
  }, i18nFromBlock);

  /* Upsell dataset backing the currently open popup. Set on every open by
     showUpsellPopup(); read by renderPopup() and the "Hinzufügen" handler. */
  var current = null;

  /* ── Constants ── */
  var SHIPPING_THRESHOLD = (function() {
    var d = document.getElementById('cart-drawer');
    return d ? parseFloat(d.getAttribute('data-threshold')) || 50 : 50;
  })();

  /* ── Helpers ── */

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2).replace('.', ',');
    if (window.__shopMoney && window.__shopMoney.format) {
      return window.__shopMoney.format
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount)
        .replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2));
    }
    return amount + ' €';
  }

  function esc(str) {
    var d = document.createElement('div');
    d.textContent = str || '';
    return d.innerHTML;
  }

  /* ── SVG icons (inline, no external deps) ── */
  var ICON = {
    check:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>',
    plus:     '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14"/><path d="M5 12h14"/></svg>',
    close:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>',
    bag:      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>',
    truck:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>',
    checkSm:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6 9 17l-5-5"/></svg>'
  };

  /* ── Render popup content ── */

  function renderPopup(addedItem, cart) {
    var totalPrice = cart.total_price / 100;
    var remaining  = Math.max(0, SHIPPING_THRESHOLD - totalPrice);
    var progress   = Math.min(100, (totalPrice / SHIPPING_THRESHOLD) * 100);
    var reached    = remaining <= 0;

    /* --- Shipping bar --- */
    var shippingHtml = '';
    if (SHIPPING_THRESHOLD > 0) {
      if (reached) {
        shippingHtml =
          '<div class="upsell-shipping reached">' +
            '<div class="upsell-shipping__text">' +
              '<span class="upsell-shipping__icon">' + ICON.checkSm + '</span>' +
              '<strong>' + T.shipping_reached + '</strong>' +
            '</div>' +
            '<div class="upsell-shipping__track"><div class="upsell-shipping__fill" style="width:100%"></div></div>' +
          '</div>';
      } else {
        var remainStr = remaining.toFixed(2).replace('.', ',') + ' €';
        shippingHtml =
          '<div class="upsell-shipping">' +
            '<div class="upsell-shipping__text">' +
              '<span class="upsell-shipping__icon">' + ICON.truck + '</span>' +
              T.shipping_remaining_html.replace('%AMOUNT%', remainStr) +
            '</div>' +
            '<div class="upsell-shipping__track"><div class="upsell-shipping__fill" style="width:' + progress + '%"></div></div>' +
          '</div>';
      }
    }

    /* --- Added item card --- */
    var variantLabel = '';
    if (addedItem.variant_title && addedItem.variant_title !== 'Default Title') {
      variantLabel = '<span class="upsell-card__variant">' + esc(addedItem.variant_title) + '</span>';
    }

    var addedPriceHtml = '';
    if (addedItem.original_price && addedItem.original_price > addedItem.price) {
      addedPriceHtml =
        '<span class="upsell-card__price upsell-card__price--sale">' + formatMoney(addedItem.price) + '</span>' +
        '<s class="upsell-card__price--compare">' + formatMoney(addedItem.original_price) + '</s>';
    } else {
      addedPriceHtml = '<span class="upsell-card__price">' + formatMoney(addedItem.price) + '</span>';
    }

    var addedHtml =
      '<div class="upsell-card upsell-card--added">' +
        '<div class="upsell-card__badge">' + ICON.check + ' ' + T.added + '</div>' +
        '<div class="upsell-card__inner">' +
          '<div class="upsell-card__img">' +
            (addedItem.image ? '<img src="' + addedItem.image + '" alt="' + esc(addedItem.product_title) + '" width="80" height="80">' : '') +
          '</div>' +
          '<div class="upsell-card__info">' +
            '<div class="upsell-card__name">' + esc(addedItem.product_title) + '</div>' +
            variantLabel +
            '<div class="upsell-card__prices">' + addedPriceHtml + '</div>' +
          '</div>' +
        '</div>' +
      '</div>';

    /* --- Upsell product card --- */
    var upsellPriceHtml = '';
    if (current.compare_at_price && current.compare_at_price > current.price) {
      upsellPriceHtml =
        '<span class="upsell-card__price upsell-card__price--sale">' + formatMoney(current.price) + '</span>' +
        '<s class="upsell-card__price--compare">' + formatMoney(current.compare_at_price) + '</s>';
    } else {
      upsellPriceHtml = '<span class="upsell-card__price">' + formatMoney(current.price) + '</span>';
    }

    var upsellTextHtml = current.text
      ? '<p class="upsell-card__text">' + esc(current.text) + '</p>'
      : '';

    var upsellHtml =
      '<div class="upsell-card upsell-card--upsell">' +
        '<div class="upsell-card__label">' + T.recommended + '</div>' +
        '<div class="upsell-card__inner">' +
          '<a href="' + current.url + '" class="upsell-card__img">' +
            '<img src="' + current.image + '" alt="' + esc(current.image_alt) + '" width="80" height="80">' +
          '</a>' +
          '<div class="upsell-card__info">' +
            '<a href="' + current.url + '" class="upsell-card__name">' + esc(current.title) + '</a>' +
            '<div class="upsell-card__prices">' + upsellPriceHtml + '</div>' +
            upsellTextHtml +
          '</div>' +
        '</div>' +
        '<button class="upsell-card__add-btn" data-upsell-add type="button">' +
          ICON.plus + ' ' + T.add +
        '</button>' +
      '</div>';

    /* --- Actions --- */
    var actionsHtml =
      '<div class="upsell-actions">' +
        '<button class="upsell-actions__cart" data-upsell-to-cart type="button">' +
          ICON.bag + ' ' + T.to_cart +
        '</button>' +
        '<button class="upsell-actions__continue" data-upsell-close type="button">' + T.continue_shopping + '</button>' +
      '</div>';

    /* --- Assemble --- (close button lives in the shell, outside the panel) */
    panel.innerHTML =
      shippingHtml +
      addedHtml +
      upsellHtml +
      actionsHtml;
  }

  /* ── Open / Close ── */

  function openPopup() {
    popup.classList.add('open');
    popup.setAttribute('aria-hidden', 'false');
    document.body.classList.add('upsell-open');
    var closeBtn = popup.querySelector('.upsell-popup__close');
    if (closeBtn) closeBtn.focus();
  }

  function closePopup() {
    popup.classList.remove('open');
    popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('upsell-open');
  }

  /* ── Update shipping bar after upsell add ── */

  function updatePopupShipping(cart) {
    var totalPrice = cart.total_price / 100;
    var remaining  = Math.max(0, SHIPPING_THRESHOLD - totalPrice);
    var progress   = Math.min(100, (totalPrice / SHIPPING_THRESHOLD) * 100);
    var reached    = remaining <= 0;

    var shippingEl = popup.querySelector('.upsell-shipping');
    if (!shippingEl) return;

    var textEl = shippingEl.querySelector('.upsell-shipping__text');
    var fillEl = shippingEl.querySelector('.upsell-shipping__fill');

    if (fillEl) fillEl.style.width = progress + '%';

    if (reached) {
      shippingEl.classList.add('reached');
      if (textEl) {
        textEl.innerHTML =
          '<span class="upsell-shipping__icon">' + ICON.checkSm + '</span>' +
          '<strong>' + T.shipping_reached + '</strong>';
      }
    } else {
      var remainStr = remaining.toFixed(2).replace('.', ',') + ' €';
      shippingEl.classList.remove('reached');
      if (textEl) {
        textEl.innerHTML =
          '<span class="upsell-shipping__icon">' + ICON.truck + '</span>' +
          T.shipping_remaining_html.replace('%AMOUNT%', remainStr);
      }
    }
  }

  /* ── Event delegation ── */

  popup.addEventListener('click', function(e) {
    var target = e.target;

    /* Close (X button, backdrop, "Weiter shoppen") */
    if (target.closest('[data-upsell-close]')) {
      e.preventDefault();
      closePopup();
      return;
    }

    /* "Zum Warenkorb" → close popup, open drawer */
    if (target.closest('[data-upsell-to-cart]')) {
      e.preventDefault();
      closePopup();
      if (typeof window.openCart === 'function') window.openCart();
      return;
    }

    /* "Hinzufügen" → add upsell to cart */
    var addBtn = target.closest('[data-upsell-add]');
    if (addBtn) {
      e.preventDefault();
      if (!current) return;
      addBtn.disabled = true;
      addBtn.innerHTML = ICON.check + ' ' + T.added;
      addBtn.classList.add('added');

      var fd = new FormData();
      fd.append('id', current.variant_id);
      fd.append('quantity', '1');

      /* Direct fetch — bypass window.addToCart to avoid recursive hook.
         After success: close popup + open cart drawer (same behavior as
         the "Zum Warenkorb" button). openCart() triggers refreshCartDrawer()
         internally, so the drawer reflects the upsell-added state. */
      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        body: fd
      })
      .then(function(r) {
        if (!r.ok) throw new Error('Add upsell failed');
        return r.json();
      })
      .then(function() {
        closePopup();
        if (typeof window.openCart === 'function') window.openCart();
      })
      .catch(function(err) {
        console.error('[upsell-popup] Add upsell failed:', err);
        addBtn.disabled = false;
        addBtn.innerHTML = ICON.plus + ' ' + T.try_again;
        addBtn.classList.remove('added');
      });
      return;
    }
  });

  /* Escape key */
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && popup.classList.contains('open')) {
      closePopup();
    }
  });

  /* ── Public API — show the popup for a given added item + upsell dataset ──
     Used by the PDP hook (below) and by assets/quick-add.js on the collection.
     Falls through to openCart() when there's no upsell or it's already in cart. */

  function showUpsellPopup(addedItem, upsellData) {
    if (!upsellData || !upsellData.id) {
      if (typeof window.openCart === 'function') window.openCart();
      return;
    }

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        /* Check if upsell product is already in cart */
        var inCart = false;
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].product_id === upsellData.id) {
            inCart = true;
            break;
          }
        }

        if (inCart) {
          /* Fallback: upsell already in cart */
          if (typeof window.openCart === 'function') window.openCart();
          return;
        }

        /* Show popup */
        current = upsellData;
        renderPopup(addedItem, cart);
        openPopup();
      })
      .catch(function(err) {
        console.error('[upsell-popup] Cart check failed:', err);
        if (typeof window.openCart === 'function') window.openCart();
      });
  }

  window.showUpsellPopup = showUpsellPopup;

  /* ── The PDP Hook — called by cart-drawer.js after add-to-cart ──
     Only registered when the viewed product actually has an upsell, so a PDP
     without one keeps the old behavior (cart-drawer.js opens the drawer directly). */
  if (pdpUpsellData && pdpUpsellData.id) {
    window._onCartAdd = function(item) {
      showUpsellPopup(item, pdpUpsellData);
    };
  }
})();
